import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Format an ISO date string or Date to "YYYY-MM-DD HH:MM:SS UTC" format
 * @param isoDate - ISO date string (e.g., "2024-01-15T14:30:45.123Z" or "2024-01-15T14:30:45+00:00") or Date object
 * @returns Formatted string like "2024-01-15 14:30:45 UTC"
 */
export function formatDateUTC(isoDate: string | Date): string {
  if (typeof isoDate === 'string') {
    // Extract YYYY-MM-DDTHH:MM:SS, ignoring milliseconds, Z, or timezone offset
    const match = isoDate.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2}:\d{2})/)
    if (match) {
      return `${match[1]} ${match[2]} UTC`
    }
    // Fallback: just replace T and strip common suffixes
    return isoDate.replace('T', ' ').replace(/[.+Z].*$/, '') + ' UTC'
  }
  // For Date objects, format using UTC methods
  const year = isoDate.getUTCFullYear()
  const month = String(isoDate.getUTCMonth() + 1).padStart(2, '0')
  const day = String(isoDate.getUTCDate()).padStart(2, '0')
  const hours = String(isoDate.getUTCHours()).padStart(2, '0')
  const minutes = String(isoDate.getUTCMinutes()).padStart(2, '0')
  const seconds = String(isoDate.getUTCSeconds()).padStart(2, '0')
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds} UTC`
}

/**
 * Format an ISO date string or Date to "YYYY-MM-DD HH:MM:SS UTC" format, minus 1 second.
 * Useful for displaying end-of-day deadlines (e.g., midnight becomes 23:59:59 of prior day).
 * @param isoDate - ISO date string or Date object
 * @returns Formatted string like "2024-01-14 23:59:59 UTC"
 */
export function formatDateUTCEndOfDay(isoDate: string | Date): string {
  const date = typeof isoDate === 'string' ? new Date(isoDate) : isoDate
  const displayDate = new Date(date.getTime() - 1000)
  return formatDateUTC(displayDate)
}
